import { JOINT_LABEL_STYLES } from "./joints/jointStyles";
import { logger } from "../utils/logger";
export const JOINT_LABEL_CLASS = "joint-label";
export const JOINT_LABEL_TEXT_CLASS = "joint-label-text";
export const JOINT_LABEL_ACTUATOR_ICON_CLASS = "joint-label-actuator-icon";
export const SITE_LABEL_CLASS = "site-label";
export const BODY_LABEL_CLASS = "body-label";
export const CONSTRAINT_LABEL_CLASS = "constraint-label";
export const INVENTORY_LABEL_CLASS = "inventory-label";

function applyLabelStyles(element) {
  element.style.setProperty("color", JOINT_LABEL_STYLES.color, "important");
  element.style.setProperty("font-size", JOINT_LABEL_STYLES.fontSize, "important");
  element.style.setProperty("font-family", JOINT_LABEL_STYLES.fontFamily, "important");
  element.style.setProperty("font-weight", JOINT_LABEL_STYLES.fontWeight, "important");
  element.style.setProperty("padding", JOINT_LABEL_STYLES.padding, "important");
  element.style.setProperty("background", JOINT_LABEL_STYLES.background, "important");
  element.style.setProperty("border-radius", JOINT_LABEL_STYLES.borderRadius, "important");
  element.style.setProperty("cursor", JOINT_LABEL_STYLES.cursor, "important");
  element.style.setProperty("user-select", JOINT_LABEL_STYLES.userSelect, "important");
  element.style.setProperty("pointer-events", JOINT_LABEL_STYLES.pointerEvents, "important");
  element.style.setProperty("border", JOINT_LABEL_STYLES.border, "important");
}

export { applyLabelStyles };

/** Flex row: lightning icon and joint / actuator name inline. */
export function applyJointLabelLayoutStyles(labelRoot) {
  labelRoot.style.setProperty("display", "inline-flex", "important");
  labelRoot.style.setProperty("align-items", "center", "important");
  labelRoot.style.setProperty("position", "relative", "important");
}

/** Update the visible joint name without removing the actuator icon node. */
export function setJointLabelDisplayedName(labelRoot, name) {
  if (!labelRoot || typeof name !== "string") return;
  const textEl = labelRoot.querySelector?.(`.${JOINT_LABEL_TEXT_CLASS}`);
  if (textEl) textEl.textContent = name;
  else labelRoot.textContent = name;
}

export function createManagedLabelElement({ className, text, draggable = false, onClick }) {
  const labelDiv = document.createElement("div");
  labelDiv.className = className;
  labelDiv.textContent = text;
  labelDiv.setAttribute("draggable", draggable ? "true" : "false");
  applyLabelStyles(labelDiv);

  if (typeof onClick === "function") {
    labelDiv.addEventListener("click", onClick);
    labelDiv.cleanupManagedLabel = () => {
      labelDiv.removeEventListener("click", onClick);
    };
  }

  return labelDiv;
}

export function createBodyLabelElement({ text = "body", onClick } = {}) {
  const labelDiv = createManagedLabelElement({
    className: BODY_LABEL_CLASS,
    text,
    onClick,
  });
  return labelDiv;
}

export function isManagedLabelClass(className) {
  return (
    className === JOINT_LABEL_CLASS ||
    className === SITE_LABEL_CLASS ||
    className === BODY_LABEL_CLASS ||
    className === CONSTRAINT_LABEL_CLASS ||
    className === INVENTORY_LABEL_CLASS
  );
}

export function getManagedLabelKind(obj) {
  if (!obj?.isCSS2DObject || !obj.element) return null;
  const className = obj.element.className;
  if (className === JOINT_LABEL_CLASS) return "joint";
  if (className === SITE_LABEL_CLASS) return "site";
  if (className === BODY_LABEL_CLASS) return "body";
  if (className === CONSTRAINT_LABEL_CLASS) return "constraint";
  if (className === INVENTORY_LABEL_CLASS) return "inventory";
  return null;
}

export function isManagedLabelObject(obj) {
  return getManagedLabelKind(obj) !== null;
}

export function cleanupManagedLabelObject(obj) {
  if (!isManagedLabelObject(obj)) return false;
  if (typeof obj.element?.cleanupManagedLabel === "function") {
    obj.element.cleanupManagedLabel();
  }
  if (typeof obj.element?.cleanupDragListeners === "function") {
    obj.element.cleanupDragListeners();
  }
  if (typeof obj.element?.remove === "function") {
    obj.element.remove();
  }
  if (obj.parent) obj.parent.remove(obj);
  return true;
}

/**
 * Detach/dispose managed CSS2D labels under `root` (joint, site, empty-body,
 * tendon/equality markers). Collects first — safe during a single `traverse`.
 */
export function disposeAllManagedLabelsInSubtree(root) {
  if (!root || typeof root.traverse !== "function") return;
  const targets = [];
  root.traverse((obj) => {
    if (isManagedLabelObject(obj)) targets.push(obj);
  });
  for (const obj of targets) {
    try {
      cleanupManagedLabelObject(obj);
    } catch (error) {
      logger.warn("[labelUtils] Failed to dispose managed label", error);
    }
  }
}
